import React from 'react';
import { Typography, Box, Stack, CircularProgress, Alert } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getMyDocumentApprovals } from '../api/documents';
import DocumentCard from '../components/DocumentCard';
import { useAuth } from '../auth/useAuth';

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();

  const { data: documents, isLoading, error } = useQuery({
    queryKey: ['my-document-approvals'],
    queryFn: getMyDocumentApprovals,
  });

  const pendingDocs = documents?.filter((d) => d.status === 'pending') ?? [];
  const completedDocs = documents?.filter((d) => d.status !== 'pending') ?? [];

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 0.5 }}>
        {user?.name ? t('dashboard.welcomeName', { name: user.name }) : t('dashboard.welcome')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('dashboard.subtitle')}
      </Typography>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('dashboard.loadError')}
        </Alert>
      )}

      {!isLoading && pendingDocs.length === 0 && completedDocs.length === 0 && (
        <Alert severity="info">
          {t('dashboard.noDocuments')}
        </Alert>
      )}

      {pendingDocs.length > 0 && (
        <>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t('dashboard.actionRequired', { count: pendingDocs.length })}
          </Typography>
          <Stack spacing={1.5} sx={{ mb: 3 }}>
            {pendingDocs.map((doc) => (
              <DocumentCard key={doc.id} document={doc} />
            ))}
          </Stack>
        </>
      )}

      {completedDocs.length > 0 && (
        <>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t('dashboard.completed', { count: completedDocs.length })}
          </Typography>
          <Stack spacing={1.5}>
            {completedDocs.map((doc) => (
              <DocumentCard key={doc.id} document={doc} />
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
};

export default DashboardPage;
